import * as React from 'react';
import { Tooltip as TooltipPrimitive } from 'radix-ui';

import { cn } from '@/shared/lib/utils';

/**
 * Not from shadcn: the full text of something cut short by an ellipsis, on
 * hover or keyboard focus - and only then.
 *
 * A table column has a set width; an email address or a lab name does not.
 * Truncating keeps the rows scannable, and this is what keeps the truncation
 * honest: whatever was hidden is one hover away.
 *
 * ── Only when something is actually hidden ──────────────────────────────────
 *
 * The check runs at the moment the tooltip would open, not once at render: a
 * column narrows when the window does, and a value that fitted a second ago
 * may not now. A tooltip repeating text that is already fully visible is
 * noise, so a cell that fits never shows one.
 *
 * "Hidden" means the element itself, or anything inside it marked `truncate`
 * / `text-ellipsis`, is wider than it is allowed to be - so a cell holding an
 * avatar beside a truncated name works the same as a cell of plain text.
 *
 * Carries its own provider, so it works in any portal without the app shell
 * having to mount one.
 */

function isOverflowing(el: HTMLElement): boolean {
  const candidates = [el, ...el.querySelectorAll<HTMLElement>('.truncate, .text-ellipsis')];
  return candidates.some((node) => node.scrollWidth > node.clientWidth + 1);
}

/**
 * The cell's readable text. Decorative parts - an avatar's initials, an icon -
 * are marked aria-hidden and left out, so the tooltip says what a screen
 * reader would rather than "U USER4 …". Separate lines (a name over an email)
 * are joined with " · ".
 */
function fullText(el: HTMLElement): string {
  const copy = el.cloneNode(true) as HTMLElement;
  copy.querySelectorAll('[aria-hidden="true"]').forEach((node) => {
    node.remove();
  });
  const blocks = [...copy.querySelectorAll<HTMLElement>('div, p')].filter(
    (node) => !node.querySelector('div, p'),
  );
  const parts = (blocks.length > 1 ? blocks : [copy])
    .map((node) => (node.textContent ?? '').replace(/\s+/g, ' ').trim())
    .filter(Boolean);
  return parts.join(' · ');
}

interface OverflowTooltipProps {
  /** The element to measure and hover; rendered as-is (Radix `asChild`). */
  children: React.ReactElement;
  /** What to show instead of the element's own text, when that differs. */
  content?: React.ReactNode;
}

export function OverflowTooltip({ children, content }: OverflowTooltipProps) {
  const ref = React.useRef<HTMLElement | null>(null);
  const [text, setText] = React.useState<React.ReactNode>(null);

  const onOpenChange = (open: boolean) => {
    const el = ref.current;
    if (!open || !el || !isOverflowing(el)) {
      setText(null);
      return;
    }
    const shown = content ?? fullText(el);
    setText(shown === '' ? null : shown);
  };

  return (
    <TooltipPrimitive.Provider delayDuration={250}>
      <TooltipPrimitive.Root open={text !== null} onOpenChange={onOpenChange}>
        <TooltipPrimitive.Trigger asChild ref={ref as React.Ref<HTMLButtonElement>}>
          {children}
        </TooltipPrimitive.Trigger>
        <TooltipPrimitive.Portal>
          <TooltipPrimitive.Content
            data-slot="overflow-tooltip"
            side="top"
            sideOffset={4}
            collisionPadding={8}
            className={cn(
              'z-50 max-w-[min(420px,calc(100vw-16px))] animate-in rounded-md bg-foreground px-2.5 py-1.5 text-[11.5px] leading-snug break-words text-background fade-in-0 zoom-in-95',
            )}
          >
            {text}
            <TooltipPrimitive.Arrow className="fill-foreground" />
          </TooltipPrimitive.Content>
        </TooltipPrimitive.Portal>
      </TooltipPrimitive.Root>
    </TooltipPrimitive.Provider>
  );
}
